import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Flag, Users, Layers, LogOut, UserCheck, Settings, Mail, Megaphone, Activity, LifeBuoy, KeyRound, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { TooltipProvider } from '@/components/ui/tooltip';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Menu } from 'lucide-react';
import { useCmsAuth } from '@/contexts/CmsAuthContext';

const navItems = [
  { to: '/cms/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
  { to: '/cms/tournaments', icon: Flag, label: 'Tournaments' },
  { to: '/cms/players', icon: UserCheck, label: 'Players' },
  { to: '/cms/clients', icon: Users, label: 'Clients' },
  { to: '/cms/tiers', icon: Layers, label: 'Tiers' },
  { to: '/cms/emails', icon: Mail, label: 'Emails' },
  { to: '/cms/newsletters', icon: Megaphone, label: 'Newsletters' },
  { to: '/cms/api-monitor', icon: Activity, label: 'API Monitor' },
  { to: '/cms/reports', icon: BarChart3, label: 'Reports' },
  { to: '/cms/support', icon: LifeBuoy, label: 'Support' },
  { to: '/cms/beta-access', icon: KeyRound, label: 'Beta Access' },
  { to: '/cms/invite-codes', icon: KeyRound, label: 'Invite Codes' },
  { to: '/cms/settings', icon: Settings, label: 'Settings' },
];

const CmsNavItem = ({ to, Icon, label, onClose }) => {
  const location = useLocation();
  const isActive = location.pathname === to || location.pathname.startsWith(to + '/');
  return (
    <NavLink to={to} onClick={onClose} data-testid={`cms-nav-${label.toLowerCase()}`}
      className={`flex items-center gap-3 rounded-lg text-sm font-medium px-3 py-2.5 transition-colors
        ${isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:bg-white/[0.08] hover:text-white'}`}>
      <Icon className="w-4 h-4" strokeWidth={1.8} /><span>{label}</span>
    </NavLink>
  );
};

const CmsSidebarFooter = ({ user, onLogout, isMobile }) => {
  if (isMobile) {
    return (
      <div className="p-3 border-t border-white/10">
        <button onClick={onLogout} className="flex items-center gap-2 text-sm text-white/50 hover:text-white/80 px-3 py-2 w-full">
          <LogOut className="w-4 h-4" /> Log Out
        </button>
      </div>
    );
  }
  return (
    <div className="p-3 border-t border-white/10">
      <div className="flex items-center gap-2.5 mb-3 px-2">
        <Avatar className="w-7 h-7">
          <AvatarFallback className="bg-emerald-600 text-[10px] font-bold text-white">
            {user?.name?.[0] || 'A'}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-medium text-white truncate">{user?.name}</p>
          <p className="text-[10px] text-white/40 truncate">{user?.email}</p>
        </div>
      </div>
      <button onClick={onLogout}
        className="flex items-center gap-2 text-xs text-white/40 hover:text-white/70 px-2 py-1.5 w-full transition-colors"
        data-testid="cms-logout">
        <LogOut className="w-3.5 h-3.5" /> Log Out
      </button>
    </div>
  );
};

const CmsSidebar = ({ onClose }) => {
  const { user, logout } = useCmsAuth();
  const navigate = useNavigate();
  const isMobile = !!onClose;

  const handleLogout = () => {
    logout();
    navigate('/', { replace: true });
  };

  return (
    <div className={`flex flex-col h-full bg-[#0f172a] text-white ${isMobile ? '' : 'w-[220px]'}`} data-testid="cms-sidebar">
      <div className="flex items-center h-14 px-5 gap-3 border-b border-white/10">
        <img src="/brand/icon-logo.png" alt="SportSweeps" className="w-7 h-7 rounded-lg object-contain flex-shrink-0" />
        <span className={`font-heading font-semibold tracking-tight text-white ${isMobile ? 'text-base' : 'text-sm'}`}>SportSweeps CMS</span>
      </div>
      <nav className="flex-1 py-3 px-3 space-y-0.5">
        {isMobile ? (
          navItems.map(({ to, icon: Icon, label }) => (
            <CmsNavItem key={to} to={to} Icon={Icon} label={label} onClose={onClose} />
          ))
        ) : (
          <TooltipProvider delayDuration={0}>
            {navItems.map(({ to, icon: Icon, label }) => (
              <CmsNavItem key={to} to={to} Icon={Icon} label={label} />
            ))}
          </TooltipProvider>
        )}
      </nav>
      <CmsSidebarFooter user={user} onLogout={handleLogout} isMobile={isMobile} />
    </div>
  );
};

export const CmsShell = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen lg:h-screen lg:overflow-hidden bg-gray-50" data-testid="cms-shell">
      <aside className="hidden lg:flex flex-shrink-0">
        <CmsSidebar />
      </aside>
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="p-0 w-[260px] bg-[#0f172a]">
          <CmsSidebar onClose={() => setMobileOpen(false)} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 flex flex-col min-w-0 lg:min-h-0 lg:overflow-hidden">
        <div className="lg:hidden sticky top-0 z-10 flex items-center h-14 px-4 border-b bg-white">
          <Button variant="ghost" size="icon" onClick={() => setMobileOpen(true)} data-testid="cms-mobile-menu">
            <Menu className="w-5 h-5" />
          </Button>
          <span className="ml-3 flex items-center gap-2"><img src="/brand/icon-logo.png" alt="SportSweeps" className="w-6 h-6 rounded-md object-contain" /><span className="font-heading text-sm font-semibold">SportSweeps CMS</span></span>
        </div>
        <div className="flex-1 lg:min-h-0 lg:overflow-y-auto">
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};
